<script lang="ts">
  import { afterUpdate, onDestroy, onMount } from "svelte";
  import ChatInput from "../../components/Chat/ChatInput.svelte";
  import ChatTopMenu from "../../components/Chat/ChatTopMenu.svelte";
  import FileMessage from "../../components/Chat/FileMessage.svelte";
  import Message from "../../components/Chat/Message.svelte";
  import MessageHistoryModal from "../../components/Chat/MessageHistoryModal.svelte";
  import { dialogTypes, serverlessRoutes } from "../../lib/constants";
  import { getWithJwt, patchWithJwt, postWithJwt, putWithJwt } from "../../lib/requests";
  import {
    getSharedAccountUserLabel,
    isSharedTrainerChatEnabled,
  } from "../../lib/sharedTrainerChat";
  import { user } from "../../stores/userStore";
  import { showAlert } from "../../stores/showAlertStore";
  import { clientChatSessionId } from "../../stores/chatSessionsStore";
  import MessageMenu from "../../components/Chat/MessageMenu.svelte";
  import { translate } from "../../lib/translate";
  import { dialogData } from "../../stores/dialogDataStore";
  import { App } from "@capacitor/app";
  import { chatMessages } from "../../stores/chatMessagesStore";
  import { clientTrainerAvatarUrl, clientTrainerId } from "../../stores/clientStores";
  import { isClient } from "../../lib/roles";
  import { trainerChatWith } from "../../stores/trainerStores";
  import { Capacitor } from "@capacitor/core";
  import { Keyboard } from "@capacitor/keyboard";
  import { isLoadingMore } from "../../stores/isLoadingMoreStore";
  import { payload } from "../../stores/payloadStore";
  import { parseDateWithTime } from "../../lib/parseDateWithTime";
  import AiSummary from "../../components/Chat/AiSummary.svelte";
  import { bookmarkedMessages } from "../../stores/bookmarkedMessages";
  import AiSuggestion from "../../components/Chat/AiSuggestion.svelte";
  import { uploadImageCloudflare } from "../../lib/cloudflare/uploadImageCloudflare";
  import {
    isVideoReady,
    uploadVideoToCloudflareStreams,
  } from "../../lib/cloudflare/uploadVideoCloudflare";
  import { ButtonComponent, IndeterminateProgressBarComponent } from "ui";
  import ChatSearch from "../../components/Chat/ChatSearch.svelte";
  import { useSocket } from "../../lib/hooks/useSocket";
  import type { IMessage } from "../../interfaces/Chat.ts";
  import { storage } from "lib";
  import { pop } from "svelte-spa-router";
  import { sharedAccountState } from "../../stores/sharedAccountStore";
  import { sharedAccountUserLabels } from "../../stores/sharedAccountUsers";

  let isFileUploading = false;
  let showAiSummary: boolean;
  let showAiSuggestion: boolean;
  let avatarUrl = "";
  let isLoading = false;
  let trainersName = "";
  let selectedMessage: Partial<IMessage> | null = null;
  let isEditingMessage: boolean = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let sessionId: number;
  let fixedReceiverId: number | null = null; // Store receiver ID when chat loads
  const messageSendingIds: number[] = [];
  let searchMode = false;
  let selectedMessageIds: number[] = [];
  let currentAiSuggestion: string | null = null;
  let androidKeyboardHeight = 0;
  let historyMessage: IMessage | null = null;
  let sharedTrainerChatEnabled = false;

  // --- Web keyboard scroll anchoring -------------------------------------
  // On the web build (Chrome/Android), `interactive-widget=resizes-content`
  // in index.html makes the LAYOUT viewport shrink when the on-screen
  // keyboard opens, which is what keeps the sticky chat bar above the
  // keyboard. The document scroll offset is preserved across that resize,
  // though, so the newest messages slide out of view behind the keyboard.
  // The native path already handles this via `keyboardWillShow` ->
  // forceScrollToBottom(); on web we key off visualViewport resizes.
  //
  // Two guards matter:
  //   * Only react to a LARGE shrink. Chrome fires visualViewport resizes
  //     for the URL bar hiding/showing too (~56-70px); the keyboard is
  //     ~250-350px. Without the threshold we'd yank the user to the bottom
  //     every time they scrolled and the URL bar collapsed.
  //   * Only scroll if they were already near the bottom. Someone who
  //     scrolled up to read history and then tapped the input should keep
  //     their place.
  let lastViewportHeight = 0;
  let wasNearBottom = true;
  let onViewportResize: (() => void) | null = null;
  const KEYBOARD_SHRINK_PX = 150;
  const NEAR_BOTTOM_PX = 120;

  const updateNearBottom = (): void => {
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    wasNearBottom =
      document.body.scrollHeight - (window.scrollY + viewportHeight) <= NEAR_BOTTOM_PX;
  };

  $: searchMode && fetchData(20000);

  const touchduration = 500;
  let total: number;
  let skip = 0;
  let unSeenMessageId: number | null = null;

  const isMine = (id: number, payload?: string | null): boolean => {
    return $user.id === id;
  };

  const handleMessageSelect = (messageId: number) => {
    const index = selectedMessageIds.indexOf(messageId);
    if (index > -1) {
      selectedMessageIds = selectedMessageIds.filter((id) => id !== messageId);
    } else {
      selectedMessageIds = [...selectedMessageIds, messageId];
    }
  };

  const openMessageHistory = (message: IMessage): void => {
    if (!sharedTrainerChatEnabled) {
      return;
    }

    historyMessage = message;
  };

  const closeMessageHistory = (): void => {
    historyMessage = null;
  };

  const getMessageSharedSenderLabel = (message: IMessage): string => {
    if (!sharedTrainerChatEnabled) {
      return "";
    }

    return getSharedAccountUserLabel(
      $sharedAccountUserLabels,
      message.sharedAccountUserId,
      message.sharedAccountUserLabel
    );
  };

  const uploadFile = async (files: File[] | null) => {
    if (!files) return;

    for (const file of files) {
      isFileUploading = true;

      let tmpPayload = "";
      const isVideo = file.type.includes("video");

      if (isVideo) {
        const uploadResult = await uploadVideoToCloudflareStreams(file, true);

        if (!uploadResult.videoUrl && uploadResult.error) {
          isFileUploading = false;
          $showAlert.message = uploadResult?.error;
          continue;
        }

        if (!uploadResult.videoUrl) {
          isFileUploading = false;
          continue;
        }

        const interval = setInterval(async (): Promise<void> => {
          const isReady = await isVideoReady(uploadResult.videoUrl);

          if (isReady === true) {
            tmpPayload = `:video:${uploadResult?.videoUrl}`;
            isFileUploading = false;
            await sendMessage(tmpPayload);
            clearInterval(interval);
          }
        }, 4000);
      } else {
        const uploadRes = await uploadImageCloudflare(file, true);
        isFileUploading = false;
        if (!uploadRes.imageUrl) return;
        tmpPayload = `:cloudflareimg:${uploadRes.imageUrl}`;
        await sendMessage(tmpPayload);
      }
    }
  };

  const sendMessage = async (payload: string, model?: string) => {
    if (isEditingMessage && selectedMessage?.id) {
      editMessageRequest(payload);
    } else {
      // Use the fixed receiver ID that was set when the chat was opened
      const otherUserId = fixedReceiverId;
      if (!otherUserId) {
        $showAlert.color = "red-400";
        $showAlert.message = "Cannot send message: recipient not found";
        return;
      }
      const url = `${serverlessRoutes.MESSAGE}`;
      const message = {
        payload,
        sessionId,
        senderId: $user.id,
      };
      const data = {
        message,
        receiverId: otherUserId,
        senderName: $user.name,
      };

      let lastError;

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await postWithJwt(url, data);
          if (!$chatMessages.find((m) => m.id === res.id)) $chatMessages = [...$chatMessages, res];
          setSeen();
          setTimeout(() => {
            forceScrollToBottom();
          });

          // Log success if it took more than one attempt
          if (attempt > 1) {
            try {
              const logUrl = serverlessRoutes.FRONTEND_LOG;
              await postWithJwt(logUrl, {
                error: `Message retry success: succeeded on attempt ${attempt}/3`,
                data: JSON.stringify({
                  messageId: res.id,
                  attempts: attempt,
                  userId: $user.id,
                  timestamp: new Date().toISOString(),
                }),
              });
            } catch (logError) {
              // Silent fail for logging
            }
          }
          break; // Success, exit retry loop
        } catch (err: any) {
          lastError = err;

          // Log each failed attempt
          try {
            const logUrl = serverlessRoutes.FRONTEND_LOG;
            await postWithJwt(logUrl, {
              error: `Message send attempt ${attempt}/3 failed: ${err.message}`,
              data: JSON.stringify({
                attempt,
                errorMessage: err.message,
                receiverId: otherUserId,
                userId: $user.id,
                willRetry: attempt < 3,
                timestamp: new Date().toISOString(),
              }),
            });
          } catch (logError) {
            // Silent fail for logging
          }

          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }
      }

      if (lastError) {
        // Log the error
        try {
          const url = serverlessRoutes.FRONTEND_LOG;
          const errorDetails = {
            message: lastError.message,
            stack: lastError.stack,
            ...lastError,
          };
          await postWithJwt(url, {
            error: JSON.stringify(errorDetails),
            data: JSON.stringify(data),
          });
        } catch (error) {
          $showAlert.color = "red-400";
          $showAlert.message = "Došlo je do greške. Poruka nije poslata";
        }

        $showAlert.color = "red-400";
        $showAlert.message = "Došlo je do greške. Poruka nije poslata";
      }
    }
  };

  const socket = useSocket({ componentName: "ChatScreen" });
  const { isConnected } = socket;

  $: sharedTrainerChatEnabled = isSharedTrainerChatEnabled($user, $sharedAccountState);

  const socketConnect = () => {
    if ($isConnected && sessionId) {
      socket.emit("subscribe", { event: "message", id: sessionId });
      socket.emit("subscribe", { event: "updateMessage", id: sessionId });

      socket.on("message", async (message: IMessage) => {
        if (!$chatMessages.find((m) => m.id === message.id))
          $chatMessages = [...$chatMessages, message];
        setSeen();
        setTimeout(() => {
          forceScrollToBottom();
        });
      });

      socket.on("updateMessage", (message: IMessage) => {
        $chatMessages = $chatMessages.map((msg) => (msg.id === message.id ? message : msg));
      });
    }
  };

  // Connect socket when it becomes available
  $: if ($isConnected && sessionId) {
    socketConnect();
  }

  const socketDisconnect = () => {
    if ($isConnected && sessionId) {
      socket.emit("unsubscribe", { event: "message", id: sessionId });
      socket.emit("unsubscribe", { event: "updateMessage", id: sessionId });
    }
  };

  const copyToClipboard = (payload: string) => {
    navigator.clipboard.writeText(payload);
    selectedMessage = null;
    $showAlert.message = `${translate("SUCCESSFULLY_COPIED")} ${translate(
      "MESSAGE"
    ).toLowerCase()}`;
  };

  const editMessage = () => {
    if (!selectedMessage || !selectedMessage.payload) return;
    $payload = selectedMessage.payload;
    isEditingMessage = true;
  };

  const editMessageRequest = async (payload: string) => {
    if (!selectedMessage || !selectedMessage.id) return;
    try {
      const url = serverlessRoutes.MESSAGE;
      const partialMessage = {
        id: selectedMessage.id,
        payload,
      };
      const res = await patchWithJwt(url, { partialMessage });
      if (res.data.message === "Success.") {
        $showAlert.message = `${translate("SUCCESSFULLY_EDITED")} ${translate("MESSAGE")}`;
        selectedMessage = null;
      }
    } catch (err) {}
  };

  const touchstart = (
    messageId: number,
    messagePayload: string,
    timestamp: any,
    isMine: boolean,
    deletedAt: string | null
  ) => {
    if (deletedAt) return;
    timer = setTimeout(() => {
      selectedMessage = {
        id: messageId,
        payload: messagePayload,
        timestamp,
        isMine,
      };
    }, touchduration);
  };

  const touchend = () => {
    if (timer) clearTimeout(timer);
  };

  const clearSlectedMessage = () => {
    selectedMessage = null;
    isEditingMessage = false;
  };

  const deleteMessage = async () => {
    if (!selectedMessage || !selectedMessage.id) return;
    try {
      const url = serverlessRoutes.MESSAGE;
      const partialMessage = {
        id: selectedMessage.id,
        deletedAt: new Date().toISOString(),
      };
      const res = await patchWithJwt(url, { partialMessage });
      if (res.data.message === "Success.") {
        $showAlert.message = `${translate("SUCCESSFULLY_DELETED")} ${translate("MESSAGE")}`;
        selectedMessage = null;
      }
    } catch (err) {}
  };

  const showConfirmDeleteDialog = () => {
    $dialogData.type = dialogTypes.CONFIRM_DELETE;
    $dialogData.data = {
      executeFunction: deleteMessage,
      title: translate("MESSAGE"),
    };
  };

  const setSeen = async () => {
    const url = `${serverlessRoutes.SESSION}/set-seen`;
    try {
      await patchWithJwt(url, { sessionId, notSeen: 0 });
    } catch (err) {}
  };

  const fetchData = async (take = 25) => {
    if (!isLoading && sessionId && sessionId !== 0) {
      isLoading = true;
      const url = `${serverlessRoutes.FETCH_MESSAGES}/v2?take=${take}&skip=${skip}&sessionId=${sessionId}`;
      try {
        const data = await getWithJwt(url);

        if (!isClient($user)) {
          unSeenMessageId = data.data.unSeenMessageId;
        }

        total = data.data.count;
        data.data.messages.forEach((msg) => {
          if (!$chatMessages.find((m) => m.id === msg.id)) {
            $chatMessages.push(msg);
            //$chatMessages = $chatMessages;
          }
        });
        $chatMessages = $chatMessages.sort((msgA, msgB) => msgA.id - msgB.id);
        // $chatMessages.forEach((message) => {

        // });
        isLoading = false;

        // forceScrollToBottom();
        setTimeout(() => {
          // console.log(data.data.cout)
          if (data.data.count >= 5) {
            forceScrollToBottom();
          }
        });
      } catch (err) {}
    }
  };

  const calculateTimeDifference = (d1: string | Date, d2: string | Date) => {
    let date1 = new Date(d1);
    let date2 = new Date(d2);

    let differenceInMilliseconds = date2.getTime() - date1.getTime();
    let differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);

    return differenceInHours >= 8;
  };

  const timeHeaderMap: any = {};

  const unsubscribeMessages = chatMessages.subscribe((res) => {
    if (res && res.length > 0) {
      // setTimeout(() => {
      //   if (window.scrollY > 300) {
      //     forceScrollToBottom();
      //   }
      // }, 1000);
      if (res.length > 1) {
        for (let i = 1; i < res.length; i++) {
          if (calculateTimeDifference(res[i - 1].createdAt, res[i].createdAt)) {
            timeHeaderMap[res[i].id] = true;
          }
        }
      }
    }
  });

  const fetchMoreData = async () => {
    $isLoadingMore = true;
    skip += 15;
    const url = `${serverlessRoutes.FETCH_MESSAGES}/v2?take=25&skip=${skip}&sessionId=${sessionId}`;
    try {
      const data = await getWithJwt(url);
      // $chatMessages = [...data.data.messages, ...$chatMessages];
      data.data.messages.forEach((msg) => {
        if (!$chatMessages.find((m) => m.id === msg.id)) {
          $chatMessages.push(msg);
          // $chatMessages = $chatMessages;
        }
      });
      $chatMessages = $chatMessages.sort((msgA, msgB) => msgA.id - msgB.id);
      $isLoadingMore = false;
    } catch (err) {
      $isLoadingMore = false;
    }
  };

  const unsubscribeAvatarUrl = clientTrainerAvatarUrl.subscribe((res) => {
    if (!res) return;
    avatarUrl = isClient($user) ? res : "/default-avatar.png";
  });

  // const unsubscribeSessionId = clientChatSessionId.subscribe((res) => {
  //   if (res) {
  //     sessionId = res;
  //     socketConnect();
  //   }
  //   if (res && skip === 0) fetchData();
  // });

  const bookmarkMessage = async () => {
    if (!selectedMessage) return;
    const url = `${serverlessRoutes.BOOKMARK}`;
    try {
      const createdAt = selectedMessage.timestamp.replace("T", " ").replace("Z", "");
      const response = await postWithJwt(url, {
        messageId: selectedMessage.id,
        sessionId,
        createdAt,
        payload: selectedMessage.payload,
      });
      const id = response.data[0];

      $bookmarkedMessages = [
        ...$bookmarkedMessages,
        {
          id,
          messageId: selectedMessage.id,
          sessionId,
          createdAt,
          payload: selectedMessage.payload,
          senderId: selectedMessage.isMine ? $user.id : $trainerChatWith.clientId,
          showBorder: false,
        },
      ];

      bookmarkedMessages.generateBorder();

      $showAlert.message = `${translate("SUCCESSFULLY_BOOKMARKED_MESSAGE")}`;
    } catch (err) {
    } finally {
      selectedMessage = null;
    }
  };

  let lastScrollHeight: number;
  let currentScrollHeight: number;
  let blockScroll = false;

  onMount(async (): Promise<void> => {
    if (isClient($user)) {
      trainersName = $user.trainer?.name ? $user.trainer?.name : "Trener";
      sessionId = $clientChatSessionId;
      if (!sessionId) {
        const storedSessionId = await storage.get("clientChatSessionId");
        sessionId = storedSessionId;
      }
      // Fix the receiver ID for clients
      const trainerId = $clientTrainerId || (await storage.get("clientTrainerId"));
      fixedReceiverId = trainerId ? Number(trainerId) : null;
      // Store it for consistency
      if (trainerId && !$clientTrainerId) {
        $clientTrainerId = trainerId;
      }
    } else {
      if (!$trainerChatWith.sessionId) {
        $trainerChatWith = await storage.get("trainerChatWith");
      }

      sessionId = $trainerChatWith.sessionId;
      trainersName = $trainerChatWith.clientName;
      $chatMessages = [];
      avatarUrl = $trainerChatWith.avatarUrl;
      // Fix the receiver ID for trainers - this should always be available from navigation
      fixedReceiverId = $trainerChatWith.clientId;
      if (!fixedReceiverId) {
        console.error("Warning: Trainer chat opened without clientId");
        // Navigate back if no recipient is set
        pop();
      }
    }
    fetchData();

    if (Capacitor.isNativePlatform()) {
      App.addListener("appStateChange", async ({ isActive }) => {
        if (isActive) {
          skip = 0;
          await fetchData();
        }
      });
    }

    // Add keyboard listeners for Android to handle message visibility
    if (Capacitor.getPlatform() === "android") {
      Keyboard.addListener("keyboardWillShow", (info) => {
        androidKeyboardHeight = info.keyboardHeight;
        setTimeout(() => {
          forceScrollToBottom();
        }, 100);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        androidKeyboardHeight = 0;
      });
    }

    // Web only: mirror the native keyboardWillShow -> scroll-to-bottom
    // behavior using the visual viewport. See the notes by `wasNearBottom`.
    if (!Capacitor.isNativePlatform() && window.visualViewport) {
      const viewport = window.visualViewport;
      lastViewportHeight = viewport.height;
      updateNearBottom();

      onViewportResize = (): void => {
        const shrunkBy = lastViewportHeight - viewport.height;
        lastViewportHeight = viewport.height;

        if (shrunkBy < KEYBOARD_SHRINK_PX || !wasNearBottom) return;

        // Once on the next frame, once after the keyboard animation settles —
        // the layout reflow lands in stages and a single call lands short.
        requestAnimationFrame(forceScrollToBottom);
        setTimeout(forceScrollToBottom, 150);
      };

      viewport.addEventListener("resize", onViewportResize);
    }

    window.onscroll = async (): Promise<void> => {
      updateNearBottom();

      if (!lastScrollHeight && !currentScrollHeight) {
        lastScrollHeight = document.body.scrollHeight;
        currentScrollHeight = document.body.scrollHeight;
      }

      if (window.scrollY <= 0) {
        if ($chatMessages.length < total && !blockScroll) {
          blockScroll = true;

          await fetchMoreData();

          currentScrollHeight = document.body.scrollHeight;

          const top = currentScrollHeight - lastScrollHeight;

          window.scrollTo({ top });

          lastScrollHeight = currentScrollHeight;
        }
      } else if (window.scrollY > 0) {
        blockScroll = false;
      }
    };

    showAiSuggestion = (await storage.get("showAiSuggestion")) === true;
    showAiSummary = (await storage.get("showAiSummary")) === true;

    // setTimeout(forceScrollToBottom);
    // setTimeout(forceScrollToBottom, 500);
    if (sessionId) setSeen();

  });

  const scrollToBottom = () => {
    if (skip !== 0) return; // Ovo mora zbog fetch load more jer se skroluje na gore
    window.scrollTo(0, document.body.scrollHeight);
  };

  const forceScrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight);
  };

  // afterUpdate(() => {
  //   if (Capacitor.isNativePlatform()) {
  //     //scrollToBottom();
  //   }
  // });

  onDestroy(async () => {
    App.removeAllListeners();
    // unsubscribeSessionId();
    unsubscribeAvatarUrl();
    unsubscribeMessages();
    socketDisconnect();

    // Clean up Android keyboard listeners
    if (Capacitor.getPlatform() === "android") {
      Keyboard.removeAllListeners();
    }

    // Clean up the web visualViewport listener
    if (onViewportResize && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", onViewportResize);
      onViewportResize = null;
    }

    await storage.remove("trainerChatWith");

    // Clear any typed message to prevent it from being sent to wrong recipient
    $payload = "";

    // Clear fixed receiver ID
    fixedReceiverId = null;
    historyMessage = null;

    window.onscroll = (): void => {};
    $bookmarkedMessages = [];
  });

  const isFile = (message: IMessage): boolean => {
    return Boolean(
      (message.files && message.files.length > 0) ||
      (message.payload && message.payload.includes(":video:")) ||
      (message.payload && message.payload.includes("cloudflarestream")) ||
      (message.payload && message.payload.includes(":cloudflareimg:"))
    );
  };
</script>

{#if $user.isChatDisabled}
  <div class="h-screen flex flex-col items-center justify-center gap-8">
    <div>Nemate pristup ovoj stranici</div>
    <ButtonComponent on:click={pop}>{translate("BACK")}</ButtonComponent>
  </div>
{:else}
  {#if !searchMode && sessionId}
    <ChatTopMenu
      bind:searchMode
      {trainersName}
      {avatarUrl}
      {sessionId}
      clientId={!isClient($user) ? $trainerChatWith.clientId : null}
    />
  {:else if searchMode}
    <ChatSearch bind:searchMode />
  {/if}
  <div class="bg-white dark:bg-zinc-800">
    <!-- {#if isLoading}
      <Spinner />
    {:else} -->
    {#if Capacitor.isNativePlatform()}
      <div class="chat-top-spacer"></div>
    {/if}
    <div class="mt-4"></div>
    <!-- {#if $chatMessages.length < total}
        <LoadMoreButton isReverse={true} {fetchMoreData} />
      {/if} -->

    <!-- <InfiniteScroll hasMore={$chatMessages.length < total} on:loadMore={fetchMoreData} reverse /> -->
    <div
      class="relative flex-col custom-height"
      style="padding-bottom: {Capacitor.getPlatform() === 'android'
        ? androidKeyboardHeight > 0
          ? androidKeyboardHeight + 44 + 'px'
          : '68px'
        : ''}"
    >
      {#each $chatMessages as message (message.id)}
        {#if isFile(message)}
          <FileMessage
            {message}
            {scrollToBottom}
            {touchstart}
            {touchend}
            isBottomImage={$chatMessages[$chatMessages.length - 1]?.id === message.id}
            isMine={isMine(message.senderId)}
            sharedSenderLabel={getMessageSharedSenderLabel(message)}
            isSharedTrainerChatEnabled={sharedTrainerChatEnabled}
            onHistoryOpen={openMessageHistory}
          />
          <!-- {scrollToBottom} -->
        {:else}
          <div class="relative flex flex-col">
            {#if timeHeaderMap[message.id]}
              <span class="text-xxs text-center mt-4 mb-2 text-slate-900 dark:text-slate-300"
                >{parseDateWithTime(message.createdAt)}</span
              >
            {/if}
            <Message
              {message}
              {touchstart}
              {touchend}
              isMine={isMine(message.senderId, message.payload)}
              isSelected={selectedMessageIds.includes(message.id)}
              onSelect={handleMessageSelect}
              sharedSenderLabel={getMessageSharedSenderLabel(message)}
              isSharedTrainerChatEnabled={sharedTrainerChatEnabled}
              onHistoryOpen={openMessageHistory}
            />
            {#if !isClient($user) && unSeenMessageId && unSeenMessageId === message.id}
              <img
                class="mt-1 mr-2 h-4 w-4 rounded-full self-end"
                src={avatarUrl ? avatarUrl : "/default-avatar.png"}
                alt="Avatar"
              />
            {/if}
          </div>
        {/if}
      {/each}
      {#if isFileUploading}
        <!-- <div class="flex flex-row items-center justify-center my-4">
            <MiniSpinner />
          </div> -->
        <div
          class="bg-white dark:bg-zinc-800 fixed bottom-[57px] left-[50%] translate-x-[-50%] w-full pt-2 max-w-[640px]"
        >
          <div class="text-center text-xxs">{translate("UPLOADING_FILE")}</div>
          <IndeterminateProgressBarComponent />
        </div>
      {/if}
    </div>
    <!-- {/if} -->
    {#if selectedMessage}
      <MessageMenu
        {bookmarkMessage}
        {copyToClipboard}
        {clearSlectedMessage}
        {editMessage}
        {showConfirmDeleteDialog}
        {selectedMessage}
      />
    {/if}
    {#if sessionId && (isClient($user) ? $clientTrainerId : $trainerChatWith.clientId)}
      <ChatInput
        {avatarUrl}
        {isFileUploading}
        clientName={trainersName}
        {sendMessage}
        {uploadFile}
      />
    {/if}
    {#if showAiSummary}
      <AiSummary {sessionId} />
    {/if}
    {#if showAiSuggestion && !isClient($user) && $chatMessages.length > 0 && $chatMessages[$chatMessages.length - 1]?.senderId !== $user.id}
      <AiSuggestion {sendMessage} {sessionId} bind:currentSuggestion={currentAiSuggestion} />
    {/if}
    <MessageHistoryModal
      isOpen={historyMessage !== null}
      message={historyMessage}
      onClose={closeMessageHistory}
    />
  </div>
{/if}

<style>
  .custom-height {
    min-height: calc(100vh - 48px - 48px);
  }
  .chat-top-spacer {
    height: calc(
      48px +
      (var(--safe-area-inset-top, env(safe-area-inset-top)) * 1.75) +
      var(--app-top-extra) +
      var(--app-tab-bottom-extra)
    );
  }
</style>
